import * as React from 'react';
import { ScrollArea as ScrollAreaPrimitive } from '@base-ui/react/scroll-area';
import { cn } from '../../lib/utils';

export interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  viewportClassName?: string;
  contentClassName?: string;
}

const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    { className, viewportClassName, contentClassName, children, ...props },
    ref,
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      data-slot='scroll-area'
      className={cn('eye:relative eye:min-h-0', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        data-slot='scroll-area-viewport'
        className={cn(
          'eye:h-full eye:max-h-[inherit] eye:rounded-[inherit] eye:focus-visible:outline-2 eye:focus-visible:-outline-offset-2 eye:focus-visible:outline-black',
          viewportClassName,
        )}
      >
        <ScrollAreaPrimitive.Content className='eye:flow-root eye:pr-16 eye:pb-16'>
          <div className={contentClassName}>{children}</div>
        </ScrollAreaPrimitive.Content>
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollBar orientation='horizontal' />
      <ScrollAreaPrimitive.Corner className='eye:bg-grey-20' />
    </ScrollAreaPrimitive.Root>
  ),
);
ScrollArea.displayName = 'ScrollArea';

interface ScrollBarProps extends React.HTMLAttributes<HTMLDivElement> {
  orientation?: 'vertical' | 'horizontal';
}

const ScrollBar = React.forwardRef<HTMLDivElement, ScrollBarProps>(
  ({ className, orientation = 'vertical', ...props }, ref) => (
    <ScrollAreaPrimitive.Scrollbar
      ref={ref}
      orientation={orientation}
      data-slot='scroll-area-scrollbar'
      className={cn(
        'eye:flex eye:touch-none eye:rounded-full eye:bg-grey-20 eye:p-[2px] eye:select-none',
        orientation === 'vertical' ? 'eye:w-12' : 'eye:h-12 eye:flex-col',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        data-slot='scroll-area-thumb'
        className='eye:relative eye:flex-1 eye:rounded-full eye:bg-grey-100'
      />
    </ScrollAreaPrimitive.Scrollbar>
  ),
);
ScrollBar.displayName = 'ScrollBar';

export { ScrollArea };
